import { on } from '@/lib/events';
import { track } from './client';
import { currentPersona } from './persona-tag';

export { track } from './client';
export { getSessionId } from './session';
export { currentPersona } from './persona-tag';
export type { EventName, EventProps } from './types';

// Lightweight wrapper that pulls in `persona` if the user has completed
// enough of the quiz to detect one. Keeps callsites clean.
function trackWithPersona(name: Parameters<typeof track>[0], props: Record<string, string | number | boolean | null> = {}): void {
  const persona = currentPersona();
  track(name, persona ? { ...props, persona } : props);
}

interface FilterAppliedPayload {
  active_filter_count: number;
  layer: string;
  matched_count: number;
}

interface PresetSelectedPayload {
  preset_id: string;
}

interface LayerChangedPayload {
  from: string;
  to: string;
}

interface PoiToggledPayload {
  category: string;
  on: boolean;
}

interface ComuneOpenedPayload {
  source: string;
  has_profile_match?: boolean;
}

interface PlaceSelectedPayload {
  level: string;
  total_selected: number;
}

interface LoginStartedPayload {
  method: string;
}

interface ScoringPresetChangedPayload {
  preset_id: string;
}

interface CompareToggledPayload {
  on: boolean;
  total: number;
}

interface CompareOpenedPayload {
  count: number;
}

interface ShortlistSaveToggledPayload {
  on: boolean;
}

interface TourStartedPayload {
  total_steps: number;
}

interface TourStepViewedPayload {
  step: number;
  selector: string;
}

interface TourCompletedPayload {
  total_steps: number;
}

interface TourSkippedPayload {
  step: number;
  total_steps: number;
}

interface RecommenderViewedPayload {
  top_region_iso: string;
  top_region_score: number;
}

interface RecommenderRegionClickedPayload {
  region_iso: string;
  rank: number;
  match_score: number;
}

interface ScoringViewedPayload {
  istat: string;
  score: number;
}

interface ScenarioSavedPayload {
  total_saved: number;
}

interface ScenarioLoadedPayload {
  index: number;
}

interface ExperimentExposedPayload {
  experiment_id: string;
  variant: string;
}

export function initAnalyticsListeners(): () => void {
  const unsubscribers: Array<() => void> = [];

  // Initial pageview
  track('pageview', { path: window.location.pathname });

  unsubscribers.push(
    on<FilterAppliedPayload>('filter:applied', (payload) => {
      if (payload) track('filter_applied', { ...payload });
    }),
    on<PresetSelectedPayload>('preset:selected', (payload) => {
      if (payload) track('preset_selected', { preset_id: payload.preset_id });
    }),
    on<LayerChangedPayload>('layer:changed', (payload) => {
      if (payload) track('layer_changed', { from: payload.from, to: payload.to });
    }),
    on<PoiToggledPayload>('poi:toggled', (payload) => {
      if (payload) track('poi_toggled', { category: payload.category, on: payload.on });
    }),
    on<ComuneOpenedPayload>('comune:opened', (payload) => {
      if (payload) {
        track('comune_opened', {
          source: payload.source,
          has_profile_match: payload.has_profile_match ?? false,
        });
      }
    }),
    on<PlaceSelectedPayload>('place:selected', (payload) => {
      if (payload) {
        track('place_selected', {
          level: payload.level,
          total_selected: payload.total_selected,
        });
      }
    }),
    on<LoginStartedPayload>('auth:login', (payload) => {
      if (payload) track('login_started', { method: payload.method });
    }),
    on<ScoringPresetChangedPayload>('scoring:preset_changed', (payload) => {
      if (payload) track('scoring_preset_changed', { preset_id: payload.preset_id });
    }),
    on<CompareToggledPayload>('compare:toggled', (payload) => {
      if (payload) track('compare_toggled', { on: payload.on, total: payload.total });
    }),
    on<CompareOpenedPayload>('compare:opened', (payload) => {
      if (payload) track('compare_opened', { count: payload.count });
    }),
    on<ShortlistSaveToggledPayload>('shortlist:save_toggled', (payload) => {
      if (payload) track('shortlist_save_toggled', { on: payload.on });
    }),
    on<TourStartedPayload>('tour:started', (payload) => {
      if (payload) track('tour_started', { total_steps: payload.total_steps });
    }),
    on<TourStepViewedPayload>('tour:step_viewed', (payload) => {
      if (payload) track('tour_step_viewed', { step: payload.step, selector: payload.selector });
    }),
    on<TourCompletedPayload>('tour:completed', (payload) => {
      if (payload) track('tour_completed', { total_steps: payload.total_steps });
    }),
    on<TourSkippedPayload>('tour:skipped', (payload) => {
      if (payload) track('tour_skipped', { step: payload.step, total_steps: payload.total_steps });
    }),
    on<RecommenderViewedPayload>('recommender:viewed', (payload) => {
      if (payload) {
        trackWithPersona('recommender_viewed', {
          top_region_iso: payload.top_region_iso,
          top_region_score: payload.top_region_score,
        });
      }
    }),
    on<RecommenderRegionClickedPayload>('recommender:region_clicked', (payload) => {
      if (payload) {
        trackWithPersona('recommender_region_clicked', {
          region_iso: payload.region_iso,
          rank: payload.rank,
          match_score: payload.match_score,
        });
      }
    }),
    on<ScoringViewedPayload>('scoring:viewed', (payload) => {
      if (payload) {
        trackWithPersona('scoring_viewed', {
          istat: payload.istat,
          score: payload.score,
        });
      }
    }),
    on<ScenarioSavedPayload>('scenario:saved', (payload) => {
      if (payload) trackWithPersona('scenario_saved', { total_saved: payload.total_saved });
    }),
    on<ScenarioLoadedPayload>('scenario:loaded', (payload) => {
      if (payload) trackWithPersona('scenario_loaded', { index: payload.index });
    }),
    on<ExperimentExposedPayload>('experiment:exposed', (payload) => {
      if (payload) {
        track('experiment_exposed', {
          experiment_id: payload.experiment_id,
          variant: payload.variant,
        });
      }
    }),
  );

  return () => unsubscribers.forEach((fn) => fn());
}
